import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions';
import AdminLayout from '../../layouts/AdminLayout';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import { MdAdd, MdEdit, MdEvent, MdReportProblem, MdQrCode, MdClose, MdDownload, MdPrint, MdUpload, MdCheckCircle } from 'react-icons/md';
import { fetch_all_locations, upload_locations_csv } from '../../api/locationsAPI';
import QRCode from 'react-qr-code';

import FileUploadField from '../../components/FileUploadField';

export default function LocationsPage() {
    const navigate = useNavigate();
    useFetchAuthorisation("user");
    useFetchUserData();

    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedQrLocation, setSelectedQrLocation] = useState(null);
    const [qrType, setQrType] = useState('room_booking'); // 'room_booking' | 'maintenance' | 'location'
    const [showCsvModal, setShowCsvModal] = useState(false);
    const [csvText, setCsvText] = useState('');
    const [csvUploading, setCsvUploading] = useState(false);
    const [csvResult, setCsvResult] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState('');

    const downloadSampleCsv = () => {
        const content = "Room,Area,Brief Description,Capacity\nFellowship Hall,Level 1,Main gathering hall,200\nSanctuary 2,Level 2,Secondary worship space,100\nUpper Room,Level 3,Prayer room,30";
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'sample_locations.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFileSelected = (files) => {
        if (files && files.length > 0) {
            const file = files[0];
            setSelectedFileName(file.name);
            const reader = new FileReader();
            reader.onload = (e) => {
                setCsvText(e.target.result || '');
            };
            reader.readAsText(file);
        }
    };


    const loadLocations = async () => {
        setLoading(true);
        const res = await fetch_all_locations();
        if (res?.status === 200) {
            setLocations(res.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadLocations();
    }, []);

    const getQrPayload = () => {
        if (!selectedQrLocation) return '';
        return JSON.stringify({
            type: qrType,
            location_id: selectedQrLocation.id,
            location_name: selectedQrLocation.name,
            timestamp: new Date().toISOString()
        });
    };

    const handlePrintQr = () => {
        window.print();
    };

    const columns = [
        {
            header: "Location Name",
            accessor: "name",
            modifier: (val, row) => (
                <div>
                    <span className="font-bold text-gray-900">{row.name}</span>
                    <p className="text-xs text-gray-500 truncate max-w-xs">{row.description || "No description"}</p>
                </div>
            )
        },
        {
            header: "Type",
            accessor: "location_type",
            modifier: (val) => (
                <span className="px-2.5 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                    {val || "Hall"}
                </span>
            )
        },
        {
            header: "Capacity",
            accessor: "capacity",
            modifier: (val) => `${val || 0} pax`
        },
        {
            header: "Booking Enabled",
            accessor: "is_booking_enabled",
            modifier: (val) => (
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                    val === "1" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                }`}>
                    {val === "1" ? "Enabled" : "Disabled"}
                </span>
            )
        },
    ];

    const handleCsvUpload = async () => {
        if (!csvText.trim()) return;
        setCsvUploading(true);
        setCsvResult(null);
        const res = await upload_locations_csv(csvText);
        setCsvUploading(false);
        if (res?.status === 200) {
            setCsvResult({ success: true, message: res.message });
            loadLocations();
            setTimeout(() => {
                setShowCsvModal(false);
                setCsvText('');
                setCsvResult(null);
            }, 1500);
        } else {
            setCsvResult({ success: false, message: res?.message || 'Upload failed' });
        }
    };

    return (
        <AdminLayout
            navSelected={"Locations & Facilities"}
            headerText={"Locations & Facilities"}
            toolbar={
                <>
                    <h1 className="text-xl font-bold text-gray-900">Locations &amp; Facilities</h1>
                    <div className="flex gap-2">
                        <Button variant="outline" icon={MdUpload} onClick={() => { setShowCsvModal(true); setCsvResult(null); setCsvText(''); }}>
                            Import CSV
                        </Button>
                        <Button variant="primary" icon={MdAdd} onClick={() => navigate('/admin/locations/details?location_id=new')}>
                            New Location
                        </Button>
                    </div>
                </>
            }
        >
            {loading ? (
                <div className="p-8 text-center text-gray-500 font-medium">Loading locations...</div>
            ) : (
                <DataTable
                    columns={columns}
                    data={locations}
                    enableActions
                    actions={(row) => (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                icon={MdQrCode}
                                onClick={() => {
                                    setSelectedQrLocation(row);
                                    setQrType('room_booking');
                                }}
                            >
                                QR Code
                            </Button>

                            {row.is_booking_enabled === "1" && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    icon={MdEvent}
                                    onClick={() => navigate(`/admin/locations/bookings/details?booking_id=new&location_id=${row.id}`)}
                                >
                                    Book
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                icon={MdReportProblem}
                                onClick={() => navigate(`/admin/locations/reports/details?report_id=new&location_id=${row.id}`)}
                            >
                                Report
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                icon={MdEdit}
                                onClick={() => navigate(`/admin/locations/details?location_id=${row.id}`)}
                            >
                                Edit
                            </Button>
                        </div>
                    )}
                />
            )}

            {/* CSV Upload Modal */}
            {showCsvModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl relative border border-gray-200 space-y-4">
                        <button
                            onClick={() => setShowCsvModal(false)}
                            className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        >
                            <MdClose size={22} />
                        </button>

                        <div className="flex items-center justify-between pr-8">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Import Locations via CSV</h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Upload a CSV file or paste content below. Expected headers: <code className="bg-gray-100 px-1 rounded text-gray-800 font-mono">Room, Area, Brief Description, Capacity</code>
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                icon={MdDownload}
                                onClick={downloadSampleCsv}
                            >
                                Download Sample CSV
                            </Button>
                        </div>

                        {/* File Upload Component */}
                        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                            <FileUploadField
                                item_index="locations_csv"
                                max_file_size={5}
                                max_files={1}
                                text="Select or Drag CSV File"
                                subtext="Supported format: .csv"
                                onChange={(files) => handleFileSelected(files)}
                            />
                        </div>

                        {/* Text Preview / Edit Area */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                CSV Content Preview / Manual Edit:
                            </label>
                            <textarea
                                rows={6}
                                value={csvText}
                                onChange={(e) => setCsvText(e.target.value)}
                                placeholder={`Room,Area,Brief Description,Capacity\nFellowship Hall,Level 1,Main gathering hall,200\nSanctuary 2,Level 2,Secondary worship space,100`}
                                className="w-full border border-gray-200 rounded-xl p-3 text-xs font-mono bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {csvResult && (
                            <div className={`p-3 rounded-lg flex items-center gap-2 text-sm font-medium ${
                                csvResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
                            }`}>
                                <MdCheckCircle size={18} />
                                {csvResult.message}
                            </div>
                        )}

                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" className="flex-1" icon={MdClose} onClick={() => setShowCsvModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="flex-1"
                                icon={MdUpload}
                                onClick={handleCsvUpload}
                                disabled={csvUploading || !csvText.trim()}
                            >
                                {csvUploading ? 'Uploading...' : 'Process & Upload Locations'}
                            </Button>
                        </div>
                    </div>
                </div>

            )}

            {/* QR Code Generator Modal */}
            {selectedQrLocation && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative border border-gray-200">
                        <button
                            onClick={() => setSelectedQrLocation(null)}
                            className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        >
                            <MdClose size={22} />
                        </button>

                        <div className="text-center mb-4">
                            <h3 className="text-lg font-bold text-gray-900">{selectedQrLocation.name}</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Generate QR Code for Mobile Scanning</p>
                        </div>

                        {/* QR Type Selector Tabs */}
                        <div className="flex bg-gray-100 p-1 rounded-xl mb-6">
                            <button
                                onClick={() => setQrType('room_booking')}
                                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                                    qrType === 'room_booking' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                                }`}
                            >
                                Room Booking
                            </button>
                            <button
                                onClick={() => setQrType('maintenance')}
                                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                                    qrType === 'maintenance' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-600'
                                }`}
                            >
                                Maintenance
                            </button>
                            <button
                                onClick={() => setQrType('location')}
                                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                                    qrType === 'location' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-600'
                                }`}
                            >
                                General Location
                            </button>
                        </div>

                        {/* QR Code Canvas Frame */}
                        <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl border border-gray-200 mb-6">
                            <div className="p-4 bg-white rounded-xl shadow-md border border-gray-100">
                                <QRCode value={getQrPayload()} size={180} />
                            </div>
                            <p className="text-[11px] font-mono text-gray-500 mt-3 text-center break-all">
                                {qrType.toUpperCase()} • ID #{selectedQrLocation.id}
                            </p>
                        </div>

                        {/* Modal Action Buttons */}
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1"
                                icon={MdPrint}
                                onClick={handlePrintQr}
                            >
                                Print QR
                            </Button>
                            <Button
                                variant="primary"
                                className="flex-1"
                                icon={MdClose}
                                onClick={() => setSelectedQrLocation(null)}
                            >
                                Done
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}
